// src/components/modals/ReadyToCloseModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, Users, MapPin } from 'lucide-react'
import TableService from '../../services/tableService'
import toast from 'react-hot-toast'
import { cn } from '../../utils/cn'

const ReadyToCloseModal = ({ 
  isOpen, 
  onClose, 
  table = null,
  onTableStatusUpdated = null,
  title = "Ready to Close"
}) => {
  const [loading, setLoading] = useState(false)

  const handleMarkAsAvailable = async () => {
    if (!table?._id) {
      toast.error('Table information is missing')
      return
    }

    try {
      setLoading(true)
      
      // Log the table object to understand its structure
      console.log('🪑 Full table object:', table)
      
      console.log('🪑 Updating table status to available:', table.number)
      
      let response;
      
      try {
        // First try the simple availability-only update
        response = await TableService.updateTableAvailability(table._id, 'available')
      } catch (error) {
        console.warn('⚠️ Simple availability update failed, trying full table update:', error.message)
        
        // Fallback: Get current table data and update with all fields
        const currentTableResponse = await TableService.getTableById(table._id)
        if (currentTableResponse?.success) {
          const currentTable = currentTableResponse.data?.table || currentTableResponse.data
          
          const fullUpdateData = {
            number: currentTable.number,
            capacity: Number(currentTable.capacity),
            branch: currentTable.branch,
            availability: 'available',
            isActive: currentTable.isActive
          }
          
          console.log('🪑 Fallback: Full table update with data:', fullUpdateData)
          response = await TableService.updateTable(table._id, fullUpdateData)
        } else {
          throw new Error('Failed to get current table data for fallback update')
        }
      }
      
      if (response?.success) {
        toast.success(`Table ${table.number} marked as available`)
        
        // Call the callback to refresh the table list
        if (onTableStatusUpdated) {
          onTableStatusUpdated(table._id, 'available')
        }
        
        onClose()
      } else {
        throw new Error(response?.message || 'Failed to update table status')
      }
    } catch (error) {
      console.error('❌ Error updating table status:', error)
      toast.error(error?.message || 'Failed to update table status')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onClose()
  }

  if (!isOpen || !table) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-bold text-lg">{table.number}</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600">Table {table.number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Table Information */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-600">Capacity: {table.capacity} people</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium",
                  table.availability === 'occupied' 
                    ? "bg-orange-100 text-orange-800" 
                    : "bg-gray-100 text-gray-800"
                )}>
                  {table.availability === 'occupied' ? 'Occupied' : table.availability}
                </div>
              </div>
            </div>
            
            {table.branchDetails?.name && (
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-600">{table.branchDetails.name}</span>
              </div>
            )}
          </div>

          {/* Message */}
          <div className="text-center mb-6">
            <p className="text-gray-700 mb-2">
              This order is completed. You can free up the table by marking it available.
            </p>
            <p className="text-sm text-gray-500">
              The table will be ready for new customers once marked as available.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsAvailable}
            disabled={loading}
            className="bg-black text-white hover:bg-gray-800"
          >
            {loading ? 'Updating...' : 'Mark as Available'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ReadyToCloseModal
